'use client';

import React, { useEffect, useState } from 'react';
import PlaceholderIcon from '@/components/common/PlaceholderIcon';
import { Button } from '@/components/ui/Button';
import Image from 'next/image';
import Link from 'next/link';
import { LogIn, Rocket } from 'lucide-react';

const NavigationBar = () => {
  // Static data to replace translations
  const navStrings = {
    items: {
      home: 'Home',
      features: 'Funzionalità',
      pricing: 'Prezzi',
    },
    useCases: {
      lawyers: 'Avvocati',
      professionals: 'Professionisti',
      accountants: 'Commercialisti',
    },
    ctas: {
      signin: 'Accedi',
      signup: 'Provalo ora',
    },
    alts: {
      openMenu: 'Apri menu',
    },
  };

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleLinkClick = () => {
    if (isMobileMenuOpen) {
      setIsMobileMenuOpen(false);
    }
  };

  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      // Set state to true if user has scrolled more than 10px
      setIsScrolled(window.scrollY > 10);
    };

    // Check initial scroll position when component mounts
    handleScroll();

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const navClasses = [
    ...(isMobileMenuOpen
      ? ['bg-white', 'bottom-0']
      : ['transition-colors', 'duration-300', 'ease-in-out']),
    ...(isScrolled && !isMobileMenuOpen ? ['bg-white/85', 'backdrop-blur-xs', 'shadow-md'] : []),
    ...['fixed', 'w-full', 'top-0', 'z-50'],
  ].join(' ');

  return (
    <nav className={navClasses}>
      <div className="max-w-6xl mx-auto px-4">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <div className="flex-shrink-0">
            <Link href="/" className="font-bold text-text-base text-2xl focus:outline-none">
              <Image
                src="/images/logo-horizontal-orange-blue.svg"
                width={192}
                height={24}
                alt="logo"
                className="inline-block mx-2"
              />
            </Link>
          </div>

          {/* Primary Nav (Hidden on Small Screens) */}
          <div className="hidden md:flex items-center space-x-1 lg:space-x-3 mt-1">
            <Link
              href="#features"
              onClick={handleLinkClick}
              className="text-gray-600 hover:text-primary-600 px-2 lg:px-3 py-2 rounded-md text-sm font-medium transition duration-150 ease-in-out"
            >
              {navStrings.items.features}
            </Link>
            <Link
              href="#pricing"
              onClick={handleLinkClick}
              className="text-gray-600 hover:text-primary-600 px-2 lg:px-3 py-2 rounded-md text-sm font-medium transition duration-150 ease-in-out"
            >
              {navStrings.items.pricing}
            </Link>
          </div>

          {/* Right Side: Language Selector, CTA & Mobile Menu Button */}
          <div className="flex items-center space-x-2">
            {/* CTA Button (Hidden on Small Screens) */}
            <div className="hidden md:block">
              <Button asChild variant={'outline'} size={'sm'}>
                <Link href={process.env.NEXT_PUBLIC_APP_URL + '/signin'} onClick={handleLinkClick}>
                  <LogIn className="w-5 h-5 mr-2" />
                  {navStrings.ctas.signin}
                </Link>
              </Button>
            </div>
            <div className="hidden md:block">
              <Button asChild variant={'accent-default'} size={'sm'}>
                <Link href={process.env.NEXT_PUBLIC_APP_URL + '/signup'} onClick={handleLinkClick}>
                  <Rocket className="w-5 h-5 mr-2" />
                  {navStrings.ctas.signup}
                </Link>
              </Button>
            </div>
            {/* Mobile Menu Button (Visible on Small Screens) */}
            <div className="md:hidden">
              <Button
                onClick={toggleMobileMenu}
                type="button"
                variant={'ghost'}
                size={'sm'}
                aria-controls="mobile-menu"
                aria-expanded={isMobileMenuOpen}
              >
                <span className="sr-only">{navStrings.alts.openMenu}</span>
                {!isMobileMenuOpen ? (
                  <PlaceholderIcon className="block h-6 w-6" pathD="M4 6h16M4 12h16M4 18h16" />
                ) : (
                  <PlaceholderIcon className="block h-6 w-6" pathD="M6 18L18 6M6 6l12 12" />
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Menu (Conditionally Rendered) */}
      {isMobileMenuOpen && (
        <div
          id="mobile-menu"
          className="md:hidden fixed top-16 left-0 right-0 bottom-0 shadow-lg py-2 z-60"
        >
          <div className="px-4 py-4 space-y-1 sm:px-3">
            <Link
              href="#problems"
              onClick={handleLinkClick}
              className="text-gray-600 hover:bg-accent-50 block px-3 py-2 rounded-md text-base font-regular"
            >
              Perché Trovanorma?
            </Link>
            <Link
              href="#useCases"
              onClick={handleLinkClick}
              className="text-gray-600 hover:bg-accent-50 block px-3 py-2 rounded-md text-base font-regular"
            >
              Casi d&apos;uso
            </Link>
            <Link
              href="#features"
              onClick={handleLinkClick}
              className="text-gray-600 hover:bg-accent-50 block px-3 py-2 rounded-md text-base font-regular"
            >
              Funzionalità
            </Link>
            <Link
              href="#pricing"
              onClick={handleLinkClick}
              className="text-gray-600 hover:bg-accent-50 block px-3 py-2 rounded-md text-base font-regular"
            >
              Prezzi
            </Link>
            <Link
              href="#comparison"
              onClick={handleLinkClick}
              className="text-gray-600 hover:bg-accent-50 block px-3 py-2 rounded-md text-base font-regular"
            >
              Trovanorma vs ChatGPT
            </Link>
            <Link
              href="#offer"
              onClick={handleLinkClick}
              className="text-gray-600 hover:bg-accent-50 block px-3 py-2 rounded-md text-base font-regular"
            >
              Offerta di lancio
            </Link>
            <Link
              href="#faq"
              onClick={handleLinkClick}
              className="text-gray-600 hover:bg-accent-50 block px-3 py-2 rounded-md text-base font-regular"
            >
              Domande frequenti
            </Link>
            <Button asChild variant="accent-default">
              <Link
                href={process.env.NEXT_PUBLIC_APP_URL + '/signup'}
                onClick={handleLinkClick}
                className="mt-2 block w-full text-center py-2 px-4 shadow-sm transition duration-300"
              >
                <Rocket className="w-5 h-5 mr-2" />
                {navStrings.ctas.signup}
              </Link>
            </Button>
            <Button asChild variant="outline">
              <Link
                href={process.env.NEXT_PUBLIC_APP_URL + '/signin'}
                onClick={handleLinkClick}
                className="mt-2 block w-full text-center py-2 px-4 shadow-sm transition duration-300"
              >
                <LogIn className="w-5 h-5 mr-2" />
                {navStrings.ctas.signin}
              </Link>
            </Button>
          </div>
        </div>
      )}
    </nav>
  );
};

export default NavigationBar;
